import { apiService } from "../../../../components/api/apiService";
import { firstResourceAccessResourceId } from "../guidUtils";

const KNOWLEDGE_BASES_PATH = "/api/FeatureHelper/knowledge-bases";

export interface KnowledgeBaseListItem {
  id: string;
  /** First GUID on the record for APIs that require `Guid` (e.g. Resource Access). */
  resourceAccessGuid: string;
  title: string;
  description: string;
  collectionNamespace: string;
  tenantId: string;
  tags: string;
  documentCount: number;
  destinationType: string;
  vectorStoreId: string;
  vaultId: string;
  knowledgeWorkspaceID: string;
  createdOn: string;
  roleAccessList: string[];
  userAccessList: string[];
  allowedDepartmentIds: string[];
}

function pick(raw: Record<string, unknown>, k: string, kAlt?: string): string {
  const v = raw[k] ?? (kAlt ? raw[kAlt] : undefined);
  return v == null ? "" : String(v);
}

function asStringArray(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v.map((x) => String(x));
}

function mapKnowledgeBase(raw: Record<string, unknown>): KnowledgeBaseListItem {
  const tags = raw.tags ?? raw.Tags;
  const id = pick(raw, "id", "Id");
  const resourceAccessGuid = firstResourceAccessResourceId([
    id,
    pick(raw, "knowledgeWorkspaceID", "KnowledgeWorkspaceID"),
    pick(raw, "knowledgeBaseId", "KnowledgeBaseId"),
    pick(raw, "vaultId", "VaultId"),
    pick(raw, "vectorStoreId", "VectorStoreId"),
    pick(raw, "uuid", "Uuid"),
  ]);
  return {
    id,
    resourceAccessGuid,
    title: pick(raw, "title", "Title"),
    description: pick(raw, "description", "Description"),
    collectionNamespace: pick(raw, "collectionNamespace", "CollectionNamespace"),
    tenantId: pick(raw, "tenantId", "TenantId"),
    tags: tags != null ? String(tags) : "",
    documentCount: Number(raw.documentCount ?? raw.DocumentCount ?? 0),
    destinationType: pick(raw, "destinationType", "DestinationType"),
    vectorStoreId: pick(raw, "vectorStoreId", "VectorStoreId"),
    vaultId: pick(raw, "vaultId", "VaultId"),
    knowledgeWorkspaceID: pick(raw, "knowledgeWorkspaceID", "KnowledgeWorkspaceID"),
    createdOn: pick(raw, "createdOn", "CreatedOn"),
    roleAccessList: asStringArray(raw.roleAccessList ?? raw.RoleAccessList),
    userAccessList: asStringArray(raw.userAccessList ?? raw.UserAccessList),
    allowedDepartmentIds: asStringArray(raw.allowedDepartmentIds ?? raw.AllowedDepartmentIds),
  };
}

export const featureHelperService = {
  async listKnowledgeBases(): Promise<KnowledgeBaseListItem[]> {
    const data = await apiService.get(KNOWLEDGE_BASES_PATH, {}, "backend");
    const success = data?.success ?? data?.Success;
    const list = data?.data ?? data?.Data;
    if (success === false) {
      const msg = data?.message ?? data?.Message ?? "Failed to load knowledge bases";
      throw new Error(typeof msg === "string" ? msg : "Failed to load knowledge bases");
    }
    if (!Array.isArray(list)) return [];
    return list.map((item: Record<string, unknown>) => mapKnowledgeBase(item));
  },
};
